import { Layers, Check, Lock, CircleDashed, CreditCard, Wallet, ShieldCheck, Rocket } from "lucide-react";
import { cn } from "@/lib/utils";
import InfoTip from "./InfoTip";
import type { PipelineRow, StageState } from "@/lib/parent/commandMetrics";
import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";
import { ModuleProgressDrilldown } from "@/components/parent/hub/ParentDrilldownSheet";

interface Props {
  rows: PipelineRow[];
  betaOnly: boolean;
  analytics: ParentAnalyticsResult;
}

const STAGE_LABELS: { key: keyof PipelineRow["stages"]; label: string; body: string }[] = [
  { key: "learn", label: "Lesson", body: "Your teen watches and reads the lesson that builds the core idea for this module." },
  { key: "checkpoint", label: "Decision Check", body: "Your teen makes a real-world decision and a short quiz checks they understand the tradeoffs." },
  { key: "unlock", label: "Module Access", body: "Once the lesson and decision check are complete, the hands-on module unlocks for your teen." },
  { key: "apply", label: "Readiness", body: "Your teen applies what they learned inside the module — this builds true readiness on the concept." },
];

const MODULE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  budget: Wallet,
  credit: CreditCard,
  scam: ShieldCheck,
  entrepreneurship: Rocket,
};

export default function ModulePipeline({ rows, betaOnly, analytics }: Props) {
  // Always show beta-aligned modules first; advanced ones become "advanced — after launch" badges.
  const visible = betaOnly ? rows.filter((r) => !r.advanced) : rows;

  return (
    <section className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-card/80 via-card/60 to-card/40 p-5 backdrop-blur-2xl shadow-[0_24px_60px_-30px_hsl(var(--primary)/0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.05)] sm:p-6">
      <div className="pointer-events-none absolute -top-24 -left-16 h-48 w-48 rounded-full bg-secondary/10 blur-3xl" />
      <div className="relative">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Layers className="h-3.5 w-3.5" />
            </span>
            <h3 className="font-heading text-base font-semibold tracking-tight text-foreground sm:text-lg">Progress Map</h3>
            <InfoTip title="Progress Map" body="Each module follows: Lesson → Decision Check → Module Access → Readiness. This shows where your teen stands." />
          </div>
        </div>

        <div className="mt-4 hidden grid-cols-[minmax(180px,1.6fr)_repeat(4,minmax(48px,1fr))_minmax(96px,auto)] items-center gap-3 px-1 text-[10px] uppercase tracking-wider text-muted-foreground sm:grid">
          <div />
          {STAGE_LABELS.map((s) => (
            <div key={s.key} className="flex items-center justify-center gap-1">
              {s.label}
              <InfoTip title={s.label} body={s.body} />
            </div>
          ))}
          <div className="text-right">Readiness</div>
        </div>

        <div className="mt-2 space-y-2">
          {visible.map((row) => {
            const Icon = MODULE_ICON[row.id] ?? Layers;
            return (
              <div key={row.id} className="rounded-2xl border border-white/[0.05] bg-background/30 px-3 py-2.5 sm:grid sm:grid-cols-[minmax(180px,1.6fr)_repeat(4,minmax(48px,1fr))_minmax(96px,auto)] sm:items-center sm:gap-3 sm:px-4 sm:py-3">
                {/* Mobile line 1 / desktop col 1: icon + full name + badge + readiness on the right */}
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className="font-heading text-sm font-semibold text-foreground leading-snug sm:whitespace-nowrap sm:overflow-hidden sm:text-ellipsis"
                      title={row.name}
                    >
                      {row.name}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-[10px]">
                      {row.beta && <span className="rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 font-medium uppercase tracking-wider text-primary">Beta</span>}
                      {row.advanced && <span className="rounded-full border border-amber-400/30 bg-amber-500/10 px-1.5 py-0.5 font-medium uppercase tracking-wider text-amber-200">Advanced</span>}
                    </p>
                  </div>
                  {/* Readiness on mobile shows inline on line 1 */}
                  <div className="sm:hidden text-right shrink-0">
                    <ModuleProgressDrilldown
                      row={row}
                      analytics={analytics}
                      trigger={
                        <button type="button" className="text-right" aria-label={`Open ${row.name} drilldown`}>
                          <p className={cn("font-heading text-sm font-semibold leading-none underline-offset-2 decoration-dotted hover:underline", row.advanced ? "text-amber-200" : row.readiness >= 70 ? "text-emerald-300" : "text-primary")}>{row.advanced ? "—" : `${row.readiness}%`}</p>
                          <p className="mt-0.5 text-[10px] text-muted-foreground leading-none">{row.status}</p>
                        </button>
                      }
                    />
                  </div>
                </div>

                {/* Mobile line 2: compact stage row across full width */}
                <div className="mt-2 flex items-center justify-between gap-2 sm:hidden">
                  {STAGE_LABELS.map((s) => (
                    <StageDot key={s.key} state={row.stages[s.key]} label={s.label} body={s.body} />
                  ))}
                </div>

                {/* Desktop: one stage per column */}
                {STAGE_LABELS.map((s) => (
                  <div key={s.key} className="hidden justify-center sm:flex">
                    <StageDot state={row.stages[s.key]} label={s.label} body={s.body} />
                  </div>
                ))}

                <div className="hidden sm:block text-right">
                  <ModuleProgressDrilldown
                    row={row}
                    analytics={analytics}
                    trigger={
                      <button type="button" className="text-right" aria-label={`Open ${row.name} drilldown`}>
                        <p className={cn("font-heading text-sm font-semibold underline-offset-2 decoration-dotted hover:underline", row.advanced ? "text-amber-200" : row.readiness >= 70 ? "text-emerald-300" : "text-primary")}>{row.advanced ? "—" : `${row.readiness}%`}</p>
                        <p className="text-[10px] text-muted-foreground">{row.status}</p>
                      </button>
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>

        {!betaOnly && rows.some((r) => r.advanced) && (
          <p className="mt-3 text-[10px] text-muted-foreground/80">Advanced modules unlock after full launch.</p>
        )}
      </div>
    </section>
  );
}

function StageDot({ state, label, body }: { state: StageState; label: string; body: string }) {
  return (
    <InfoTip title={label} body={body}>
      <button type="button" aria-label={label} className="flex h-6 w-6 items-center justify-center">
        {state === "done" && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/40">
            <Check className="h-3 w-3" />
          </span>
        )}
        {state === "partial" && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-primary/60">
            <span className="h-2 w-2 rounded-full bg-primary" />
          </span>
        )}
        {state === "locked" && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border/40 bg-muted/30 text-muted-foreground">
            <Lock className="h-2.5 w-2.5" />
          </span>
        )}
        {state === "empty" && <CircleDashed className="h-5 w-5 text-muted-foreground/50" />}
      </button>
    </InfoTip>
  );
}